import {
  memo, useState, useCallback, useMemo,
} from 'react';
import { motion } from 'framer-motion';
import RSVPForm from '@/components/RSVPForm';
import { useToastContext } from '@/hooks/useToastContext';
import { formatDate } from '@/utils/date';
import { getThemeStyles } from '@/utils/theme';
import type { InviteData } from '@/types/invite';
import styles from './StandardInviteLayout.module.css';

type StandardInviteLayoutProps = {
  invite: InviteData;
};

/**
 * Стандартный layout для всех стандартных тем
 *
 * Использует ThemeStyles для применения разных визуальных стилей (skins)
 * Один layout, разные CSS модули из themes/standard/
 */
const StandardInviteLayout = memo(({ invite }: StandardInviteLayoutProps) => {
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const { showSuccess } = useToastContext();

  const handleRSVPSuccess = useCallback(() => {
    setRsvpSubmitted(true);
    showSuccess('Спасибо за ответ! Мы очень рады, что вы будете с нами!');
  }, [showSuccess]);

  const formattedDate = useMemo(
    () => (invite.weddingDate ? formatDate(invite.weddingDate) : ''),
    [invite.weddingDate],
  );

  // Получаем стили для текущей стандартной темы
  const themeStyles = useMemo(
    () => getThemeStyles(invite.template),
    [invite.template],
  );

  const containerClassName = [
    themeStyles.container,
    styles.container,
  ].filter(Boolean).join(' ');

  const themeName = invite.template || 'classic';

  return (
    <div className={containerClassName} data-theme={themeName}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={`${themeStyles.content} ${styles.content}`}
      >
        {/* Декоративный элемент сверху */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className={`${themeStyles.decorativeTop} ${styles.decorativeTop}`}
        />

        {/* Заголовок с именами */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className={styles.header}
        >
          <h1 className={`${themeStyles.title} ${styles.title}`}>Приглашение</h1>
          <div className={styles.names}>
            <span className={`${themeStyles.name || ''} ${styles.name}`}>
              {invite.brideName}
            </span>
            <span className={`${themeStyles.and} ${styles.and}`}>&</span>
            <span className={`${themeStyles.name || ''} ${styles.name}`}>
              {invite.groomName}
            </span>
          </div>
        </motion.div>

        {/* Основная информация */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className={styles.info}
        >
          <div className={`${themeStyles.infoItem || ''} ${styles.infoItem}`}>
            <div className={styles.infoIcon}>📅</div>
            <div>
              <div className={`${themeStyles.infoLabel || ''} ${styles.infoLabel}`}>
                Дата
              </div>
              <div className={`${themeStyles.infoValue || ''} ${styles.infoValue}`}>
                {formattedDate}
              </div>
            </div>
          </div>

          <div className={`${themeStyles.infoItem || ''} ${styles.infoItem}`}>
            <div className={styles.infoIcon}>📍</div>
            <div>
              <div className={`${themeStyles.infoLabel || ''} ${styles.infoLabel}`}>
                Место
              </div>
              <div className={`${themeStyles.infoValue || ''} ${styles.infoValue}`}>
                {invite.weddingLocation}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Пригласительный текст */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className={`${themeStyles.invitationText || ''} ${styles.invitationText}`}
        >
          Мы будем рады видеть вас на нашем торжестве!
        </motion.p>

        {/* Декоративный элемент снизу */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className={`${themeStyles.decorativeBottom} ${styles.decorativeBottom}`}
        />

        {/* Форма RSVP */}
        {!rsvpSubmitted ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <RSVPForm inviteId={invite.id} onSuccess={handleRSVPSuccess} />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className={styles.successMessage}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
              className={`${themeStyles.checkmark} ${styles.checkmark}`}
            >
              ✓
            </motion.div>
            <h2 className={themeStyles.successMessageTitle || styles.successMessageTitle}>
              Спасибо за ответ!
            </h2>
            <p className={themeStyles.successMessageText || styles.successMessageText}>
              Мы очень рады, что вы будете с нами!
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
});

StandardInviteLayout.displayName = 'StandardInviteLayout';

export default StandardInviteLayout;
